import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PlusCircleIcon from "@assets/svg/PlusCircleIcon";
import { getInterviewDetails } from "@endpoints/interview.service";
import {
  IGeneralSlotResponse,
  IGetInterviewByIdResponse,
} from "@endpoints/types/interview.type";
import { RootState } from "@store/reducers";
import { EnumUserTypes, HiringCompanyType, userTypes } from "@utils/constants";
import dayjs from "@lib/dayjs";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LatestProposalCard from "./components/LatestProposalCard";
import PastProposalCard from "./components/PastProposalCard";
import { isDeepEqual } from "@utils/helperFunction";
import InviteConfirmationContainer from "./components/InviteConfirmationContainer";
import store from "@store/index";
import SpecificSlotProposal from "./components/SpecificSlotProposal";
import { MeetingModeEnum } from "@type/meeting.type";
import LinkIcon from "@assets/svg/LinkIcon";
import CopyIcon from "@assets/svg/CopyIcon";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";

export interface IAvailabilitySlot extends IGeneralSlotResponse {
  isDeclined?: boolean;
  isAccepted?: boolean;
  isDeclinedTemp?: boolean;
  isAcceptedTemp?: boolean;
}

export const MeetingParties = [
  EnumUserTypes.CANDIDATE,
  EnumUserTypes.RECRUITER,
  EnumUserTypes.HIRING_COMPANY,
];

export interface TypeGroupOfProposals {
  createdAt: string;
  slots: IGeneralSlotResponse[];
}

function keyExistInObject({
  object,
  key,
}: {
  object: Record<string, IGeneralSlotResponse[]>;
  key: string;
}) {
  const keyExist = Object.entries(object).find(
    ([itemKey, _]) => Math.abs(dayjs(itemKey).diff(dayjs(key), "seconds")) < 10
  );
  return keyExist ? keyExist[0] : null;
}

export function separatePastAndNewProposals(slots: IGeneralSlotResponse[]) {
  if (!Array.isArray(slots)) return [];
  const separatedSlotObject: Record<string, IGeneralSlotResponse[]> = {};
  for (let slot of slots) {
    const key = slot?.createdAt;
    const updatedSlot = { ...slot, status: getSlotStatus({ slot }) };
    const existingKey = keyExistInObject({
      object: separatedSlotObject,
      key,
    });
    if (existingKey) {
      separatedSlotObject[existingKey].push(updatedSlot);
    } else {
      separatedSlotObject[key] = [updatedSlot];
    }
  }

  const sortedByCreatedAt = Object.entries(separatedSlotObject).sort(
    ([keyA], [keyB]) => dayjs(keyB).valueOf() - dayjs(keyA).valueOf()
  );
  let finalArray = [];
  sortedByCreatedAt?.forEach((item) => {
    finalArray.push({
      createdAt: item[0],
      slots: item[1],
    });
  });

  return finalArray;
}

export type slotStatus = "Accepted" | "Declined" | "Pending" | "Auto-Declined";

const getHiringTeamMemberAvailability = (slot: IGeneralSlotResponse) => {
  const currentUser = store.getState()?.auth?.user;
  const currentUserId = currentUser?._id || currentUser?.userId;

  if (!currentUserId || !Array.isArray(slot?.hiringTeamAvailability)) {
    return undefined;
  }

  const memberAvailability = slot.hiringTeamAvailability.find((item) => {
    const memberId =
      typeof item?.memberId === "string" ? item.memberId : item?.memberId?._id;
    return String(memberId) === String(currentUserId);
  });

  return memberAvailability?.isAvailable;
};

const isCurrentUserAssignedHiringMember = (slot: IGeneralSlotResponse) => {
  const currentUser = store.getState()?.auth?.user;
  const currentUserId = currentUser?._id || currentUser?.userId;

  if (currentUser?.userType !== EnumUserTypes.HIRING_COMPANY) {
    return false;
  }

  if (!currentUserId || !Array.isArray(slot?.hiringTeamAvailability)) {
    return false;
  }

  return !!slot.hiringTeamAvailability.find((item) => {
    const memberId =
      typeof item?.memberId === "string" ? item.memberId : item?.memberId?._id;
    return String(memberId) === String(currentUserId);
  });
};

export function getSlotStatus({
  slot,
  statusOf,
  isAssignedHiringTeamMember,
}: {
  slot: IGeneralSlotResponse;
  statusOf?: EnumUserTypes;
  isAssignedHiringTeamMember?: boolean;
}): slotStatus {
  const currentUser = store.getState()?.auth?.user;
  const targetStatusOf = statusOf ?? currentUser?.userType;
  const isHiringTeamMember =
    typeof isAssignedHiringTeamMember === "boolean"
      ? isAssignedHiringTeamMember
      : isCurrentUserAssignedHiringMember(slot);
  const hiringTeamMemberAvailability = getHiringTeamMemberAvailability(slot);
  const isProposedBy = slot?.proposedBy?.userType;
  const getHiringTeamMemberStatus = () => {
    if (!isHiringTeamMember) return undefined;

    if (hiringTeamMemberAvailability === true) return "Accepted";
    if (hiringTeamMemberAvailability === false) return "Declined";
    return "Pending";
  };

  if (isProposedBy === EnumUserTypes.RECRUITER) {
    if (targetStatusOf === EnumUserTypes.HIRING_COMPANY && isHiringTeamMember) {
      const hiringTeamMemberStatus = getHiringTeamMemberStatus();
      if (hiringTeamMemberStatus) return hiringTeamMemberStatus;
    }

    if (
      (!slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === false &&
        targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
      (!slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === false &&
        targetStatusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Declined";
    } else if (
      (slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === true &&
        targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
      (slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === true &&
        targetStatusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Accepted";
    } else if (
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCandidateAvailable === false) &&
        slot?.isCompanyAvailable !== false &&
        targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isCandidateAvailable !== false &&
        targetStatusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Auto-Declined";
    } else {
      return "Pending";
    }
  }

  if (isProposedBy === EnumUserTypes.CANDIDATE) {
    if (targetStatusOf === EnumUserTypes.HIRING_COMPANY && isHiringTeamMember) {
      const hiringTeamMemberStatus = getHiringTeamMemberStatus();
      if (hiringTeamMemberStatus) return hiringTeamMemberStatus;
    }

    if (
      (!slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === false &&
        targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
      (!slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === false &&
        targetStatusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Declined";
    } else if (
      (slot?.isCompanyAvailable &&
        slot?.isCompanyAvailable === true &&
        targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
      (slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === true &&
        targetStatusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Accepted";
    } else if (
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCandidateAvailable === false) &&
        slot?.isCompanyAvailable !== false &&
        targetStatusOf === EnumUserTypes.HIRING_COMPANY) ||
      ((slot?.isCandidateAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isRecruiterAvailable !== false &&
        targetStatusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Auto-Declined";
    } else {
      return "Pending";
    }
  }

  if (isProposedBy === EnumUserTypes.HIRING_COMPANY) {
    if (targetStatusOf === EnumUserTypes.HIRING_COMPANY) {
      if (isHiringTeamMember) {
        const hiringTeamMemberStatus = getHiringTeamMemberStatus();
        if (hiringTeamMemberStatus) return hiringTeamMemberStatus;
      }
      if (slot?.isCompanyAvailable === true) return "Accepted";
      if (slot?.isCompanyAvailable === false) return "Declined";
    }

    if (
      (!slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === false &&
        targetStatusOf === EnumUserTypes.CANDIDATE) ||
      (!slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === false &&
        targetStatusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Declined";
    } else if (
      (slot?.isCandidateAvailable &&
        slot?.isCandidateAvailable === true &&
        targetStatusOf === EnumUserTypes.CANDIDATE) ||
      (slot?.isRecruiterAvailable &&
        slot?.isRecruiterAvailable === true &&
        targetStatusOf === EnumUserTypes.RECRUITER)
    ) {
      return "Accepted";
    } else if (
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isRecruiterAvailable !== false &&
        targetStatusOf === EnumUserTypes.RECRUITER) ||
      ((slot?.isRecruiterAvailable === false ||
        slot?.isCompanyAvailable === false) &&
        slot?.isCandidateAvailable !== false &&
        targetStatusOf === EnumUserTypes.CANDIDATE)
    ) {
      return "Auto-Declined";
    } else {
      return "Pending";
    }
  }
}

export function extractProposalGroupAndAvailabilities(
  generalAvailabilitySlots: any,
  isAssignedHiringTeamMember?: boolean
) {
  // Grouping availabilities from recent to oldest
  const proposalGroup = separatePastAndNewProposals(generalAvailabilitySlots);
  if (proposalGroup?.length) {
    const formattedAvailabilitySlots: IAvailabilitySlot[] =
      proposalGroup[0]?.slots?.map((slot) => {
        const slotStatus = getSlotStatus({
          slot,
          statusOf: store.getState().auth?.user?.userType,
          isAssignedHiringTeamMember,
        });
        const tailoredSlot = { ...slot };
        if (slotStatus === "Pending") {
          tailoredSlot.isAccepted = false;
          tailoredSlot.isDeclined = false;
          tailoredSlot.isAcceptedTemp = false;
          tailoredSlot.isDeclinedTemp = false;
        } else if (slotStatus === "Accepted") {
          tailoredSlot.isAccepted = true;
          tailoredSlot.isDeclined = false;
          tailoredSlot.isAcceptedTemp = true;
          tailoredSlot.isDeclinedTemp = false;
        } else if (slotStatus === "Declined") {
          tailoredSlot.isDeclined = true;
          tailoredSlot.isAccepted = false;
          tailoredSlot.isDeclinedTemp = true;
          tailoredSlot.isAcceptedTemp = false;
        }
        return tailoredSlot;
      });
    return { proposalGroup, formattedAvailabilitySlots };
    // availabilitySlotsRef.current = formattedAvailabilitySlots;
    // setAvailabilitySlots(formattedAvailabilitySlots);
  }
}

const MeetingPollDetailPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const availabilitySlotsRef = useRef<IAvailabilitySlot[]>([]);
  const { pathname } = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const interviewId = searchParams.get("id");

  // Local states
  const [interviewDetails, setInterviewDetails] =
    useState<IGetInterviewByIdResponse>();
  const [isFetchingInterviewDetails, setIsFetchingInterviewDetails] =
    useState(true);
  const [availabilitySlots, setAvailabilitySlots] = useState<
    IAvailabilitySlot[]
  >([]);

  const [groupOfProposals, setGroupOfProposals] = useState<
    TypeGroupOfProposals[]
  >([]);

  const fetchInterviewDetails = async () => {
    try {
      setIsFetchingInterviewDetails(true);
      const { success, data } = await getInterviewDetails(interviewId);
      if (success) {
        if (data?.slotSelectionType === "general") {
          const currentUserId = user?._id || user?.userId;
          const isAssignedHiringTeamMember = !!data?.hiringTeamMembers?.some(
            (member) => {
              const memberUserId =
                typeof member?.userId === "string"
                  ? member.userId
                  : member?.userId?._id;
              return String(memberUserId) === String(currentUserId);
            }
          );
          const { proposalGroup, formattedAvailabilitySlots } =
            extractProposalGroupAndAvailabilities(
              data?.generalAvailabilitySlots,
              isAssignedHiringTeamMember
            );
          availabilitySlotsRef.current = formattedAvailabilitySlots;
          setAvailabilitySlots(formattedAvailabilitySlots);

          setGroupOfProposals(proposalGroup);
        } else if (data?.slotSelectionType === "specific") {
        }
        setInterviewDetails(data);
      }
    } catch (error) {
      console.error("Error: fetching interview details", error);
    } finally {
      setIsFetchingInterviewDetails(false);
    }
  };

  const handleCounterProposalPoll = () => {
    if (isEverySlotsDeclined) {
      navigate(`/counter-proposal-poll?id=${interviewId}`);
    } else {
      enqueueSnackbar(
        "You must decline all the availabilities to propose new time slots.",
        { variant: "error" }
      );
    }
  };

  const isHiringCompanyInvolved = useMemo(
    () => !!interviewDetails?.hiringCompanyId,
    [interviewDetails?.hiringCompanyId]
  );

  const isAssignedHiringTeamMember = useMemo(() => {
    const currentUserId = user?._id || user?.userId;
    if (!currentUserId) return false;

    return !!interviewDetails?.hiringTeamMembers?.some((member) => {
      const memberUserId =
        typeof member?.userId === "string"
          ? member.userId
          : member?.userId?._id;
      return String(memberUserId) === String(currentUserId);
    });
  }, [interviewDetails?.hiringTeamMembers, user?._id, user?.userId]);

  const hasAllAssignedHiringTeamResponded = useMemo(() => {
    const assignedMemberIds =
      interviewDetails?.hiringTeamMembers
        ?.map((member) => {
          const memberUserId =
            typeof member?.userId === "string"
              ? member.userId
              : member?.userId?._id;
          return memberUserId ? String(memberUserId) : null;
        })
        .filter(Boolean) || [];

    if (!assignedMemberIds.length) return true;

    return availabilitySlots?.every((slot) =>
      assignedMemberIds.every((memberId) =>
        slot?.hiringTeamAvailability?.some((availability) => {
          const availabilityMemberId =
            typeof availability?.memberId === "string"
              ? availability.memberId
              : availability?.memberId?._id;
          return (
            String(availabilityMemberId) === memberId &&
            availability?.isAvailable !== undefined
          );
        })
      )
    );
  }, [availabilitySlots, interviewDetails?.hiringTeamMembers]);

  const isInviteSend = useMemo(
    () => interviewDetails?.isInviteSend,
    [interviewDetails]
  );

  const canForwardToRecruiterAgency = useMemo(() => {
    const hiringCompanyRole = Number(user?.hiringCompanyType);
    const currentUserId = user?._id || user?.userId;
    const meetingCreatorId =
      typeof interviewDetails?.meetingCreator === "string"
        ? interviewDetails?.meetingCreator
        : interviewDetails?.meetingCreator?._id;
    const isManagerMeetingCreator =
      hiringCompanyRole === HiringCompanyType.MANAGER &&
      String(meetingCreatorId) === String(currentUserId);
    const isHiringCompanyForwarder =
      user?.userType === EnumUserTypes.HIRING_COMPANY &&
      (
        [HiringCompanyType.ADMIN, HiringCompanyType.SUPER_ADMIN].includes(
          hiringCompanyRole
        ) ||
        isManagerMeetingCreator
      );

    return (
      isHiringCompanyForwarder &&
      interviewDetails?.interviewStatus === "Sent to Team Member" &&
      hasAllAssignedHiringTeamResponded &&
      !isInviteSend
    );
  }, [
    hasAllAssignedHiringTeamResponded,
    interviewDetails?.meetingCreator,
    interviewDetails?.interviewStatus,
    isInviteSend,
    user?.hiringCompanyType,
    user?._id,
    user?.userId,
    user?.userType,
  ]);

  const isLatestProposalByMe = useMemo(() => {
    const latestProposal = groupOfProposals[0]?.slots[0];
    return (
      latestProposal?.proposedBy?._id ===
      (user?.userType === userTypes.RECRUITER ||
      user?.userType === userTypes.HIRING_COMPANY
        ? user?._id
        : user?.userId)
    );
  }, [groupOfProposals, user]);

  const meetingProposedBy = useMemo(
    () => availabilitySlots[0]?.proposedBy,
    [availabilitySlots]
  );

  const isEverySlotsDeclined = useMemo(() => {
    return availabilitySlots?.every(
      (item) =>
        item?.isCandidateAvailable === false ||
        item?.isRecruiterAvailable === false ||
        item?.isCompanyAvailable === false
    );
  }, [availabilitySlots]);

  const interviewStageBadge = useMemo(() => {
    const badgeClassMap = {
      waiting: "bg-amber-100 text-amber-700 border-amber-200",
      info: "bg-blue-100 text-blue-700 border-blue-200",
      success: "bg-emerald-100 text-emerald-700 border-emerald-200",
      danger: "bg-rose-100 text-rose-700 border-rose-200",
      neutral: "bg-slate-100 text-slate-600 border-slate-200",
    } as const;
    const hasCandidateResponded = availabilitySlots?.some(
      (slot) => slot?.isCandidateAvailable !== undefined
    );

    switch (interviewDetails?.interviewStatus) {
      case "Sent to Team Member":
        return user?.userType === EnumUserTypes.HIRING_COMPANY
          ? {
              label: "Waiting for hiring team",
              className: badgeClassMap.waiting,
            }
          : null;
      case "Sent to Recruiter":
        return user?.userType === EnumUserTypes.HIRING_COMPANY
          ? {
              label: "Forwarded to recruiter agency",
              className: badgeClassMap.info,
            }
          : {
              label: "Waiting for recruiter review",
              className: badgeClassMap.waiting,
            };
      case "Pending Acceptance":
        if (user?.userType === EnumUserTypes.HIRING_COMPANY) {
          if (!hasCandidateResponded) {
            return {
              label: "Forwarded to candidate",
              className: badgeClassMap.info,
            };
          }

          return hasAllAssignedHiringTeamResponded
            ? {
                label: "Candidate responded - awaiting final confirmation",
                className: badgeClassMap.waiting,
              }
            : {
                label: "Candidate responded - waiting for hiring team",
                className: badgeClassMap.waiting,
              };
        }

        if (user?.userType === EnumUserTypes.RECRUITER) {
          if (!hasCandidateResponded) {
            return {
              label: "Waiting for candidate response",
              className: badgeClassMap.waiting,
            };
          }

          return hasAllAssignedHiringTeamResponded
            ? {
                label: "Candidate responded - ready for final invite",
                className: badgeClassMap.info,
              }
            : {
                label: "Candidate responded - waiting for hiring team",
                className: badgeClassMap.waiting,
              };
        }

        if (user?.userType === EnumUserTypes.CANDIDATE) {
          return hasCandidateResponded
            ? {
                label: "Response sent - awaiting recruiter action",
                className: badgeClassMap.waiting,
              }
            : {
                label: "Waiting for your response",
                className: badgeClassMap.waiting,
              };
        }

        return null;
      case "Meeting Scheduled":
        return {
          label: "Meeting scheduled",
          className: badgeClassMap.success,
        };
      case "Declined":
        return {
          label: "Declined",
          className: badgeClassMap.danger,
        };
      case "Closed":
        return {
          label: "Closed",
          className: badgeClassMap.neutral,
        };
      default:
        return null;
    }
  }, [availabilitySlots, interviewDetails?.interviewStatus, user?.userType]);

  useEffect(() => {
    if (interviewId) fetchInterviewDetails();
  }, [interviewId]);

  console.log("details", availabilitySlotsRef.current, availabilitySlots);

  return (
    <div className="flex flex-col flex-1 items-center p-4 rounded-md bg-white">
      <div className="w-[75%] flex flex-col flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-center relative pt-3">
          <button
            onClick={() => {
              if (interviewDetails?.slotSelectionType === "general") {
                navigate("/calendar/meeting/scheduling");
              } else {
                navigate("/calendar/meeting/schedules");
              }
            }}
            className={`flex items-center justify-center bg-white font-medium text-xs rounded-md text-light-black absolute left-0`}
          >
            <ChevronLeftIcon className={"w-4 h-4"} fill={"gray"} />
            Back to listing
          </button>
          <div className="flex flex-col items-center gap-2">
            <span className="font-semibold text-charcoal">
              Meeting{" "}
              {interviewDetails?.slotSelectionType === "general"
                ? "time poll"
                : "invite"}
            </span>
            {interviewStageBadge ? (
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold ${interviewStageBadge.className}`}
              >
                {interviewStageBadge.label}
              </span>
            ) : null}
          </div>
        </div>

        {/* Main body */}
        {isFetchingInterviewDetails ? (
          <div className="flex flex-col flex-1 items-center justify-center">
            <LoadingAnimation className="'w-6 h-6" color="#2e2e2e" />
          </div>
        ) : (
          <div className="flex flex-col flex-1 overflow-y-scroll min-h-0 no-scrollbar">
            {/* Meeting details */}
            <div className="flex flex-col gap-3.5 mt-9">
              <div className="flex items-center">
                <span className="text-xs font-semibold">Organizer :&nbsp;</span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.createdBy?.firstName}{" "}
                  {interviewDetails?.createdBy?.lastName}
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-xs font-semibold">
                  Meeting Title :&nbsp;
                </span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.meetingTitle}
                </span>
              </div>

              <div className="flex items-center">
                <span className="text-xs font-semibold">
                  Meeting Type :&nbsp;
                </span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.meetingMode}
                  {/* {MeetingModeEnum[interviewDetails?.meetingMode]} */}
                </span>
              </div>

              {MeetingModeEnum[interviewDetails?.meetingMode] ===
              MeetingModeEnum["Virtual Meeting"] ? (
                <div className="flex items-center">
                  <span className="text-xs font-semibold">
                    Meeting URL :&nbsp;
                  </span>
                  <div className="flex items-center border-[1.5px] rounded-md w-max h-8 px-1 ml-2 gap-2">
                    <LinkIcon className={"h-4 w-4"} fill={"gray"} />
                    <span className="text-xs font-medium text-light-black line-clamp-1">
                      {interviewDetails?.url}
                    </span>
                    <CopyToClipboard
                      text={interviewDetails?.url}
                      onCopy={() =>
                        enqueueSnackbar("Link copied successfully.", {
                          variant: "success",
                        })
                      }
                    >
                      <button className="h-6 w-6 rounded-sm bg-primary flex items-center justify-center">
                        <CopyIcon className={"w-3 h-3"} fill="white" />
                      </button>
                    </CopyToClipboard>
                  </div>
                </div>
              ) : MeetingModeEnum[interviewDetails?.meetingMode] ===
                MeetingModeEnum["Face-to-Face"] ? (
                <div className="flex items-center">
                  <span className="text-xs font-semibold">
                    Meeting Address :&nbsp;
                  </span>
                  <span className="text-xs font-medium text-light-black">
                    {interviewDetails?.address}
                  </span>
                </div>
              ) : MeetingModeEnum[interviewDetails?.meetingMode] ===
                MeetingModeEnum["Telephone Call"] ? (
                <div className="flex items-center">
                  <span className="text-xs font-semibold">
                    Agent will call on :&nbsp;
                  </span>
                  <span className="text-xs font-medium text-light-black">
                    {interviewDetails?.url || "+447480734898"}
                  </span>
                </div>
              ) : null}

              <div className="flex flex-col gap-2">
                <span className="text-xs font-semibold">
                  Meeting Description :&nbsp;
                </span>
                <span className="text-xs font-medium text-light-black">
                  {interviewDetails?.meetingDescription}
                </span>
              </div>
            </div>
            {/* InviteConfirmationContainer */}
            {isInviteSend ? (
              <InviteConfirmationContainer
                interviewDetails={interviewDetails}
                setInterviewDetails={setInterviewDetails}
                slot={interviewDetails?.specificSlot}
              />
            ) : null}

            {interviewDetails?.slotSelectionType === "specific" &&
            !isInviteSend ? (
              <SpecificSlotProposal
                meetingProposedBy={meetingProposedBy}
                isEverySlotsDeclined={isEverySlotsDeclined}
                interviewDetails={interviewDetails}
                setInterviewDetails={setInterviewDetails}
                availabilitySlots={availabilitySlots}
                setAvailabilitySlots={setAvailabilitySlots}
                groupOfProposals={groupOfProposals}
                isHiringCompanyInvolved={isHiringCompanyInvolved}
                availabilitySlotsRef={availabilitySlotsRef}
                isInviteSend={isInviteSend}
                hasChanges={
                  !isDeepEqual(availabilitySlotsRef.current, availabilitySlots)
                }
              />
            ) : null}

            {/* Latest Proposal */}
            {((canForwardToRecruiterAgency &&
              interviewDetails?.slotSelectionType === "general") ||
              (!isLatestProposalByMe &&
                !isInviteSend &&
                interviewDetails?.slotSelectionType === "general")) ? (
              <LatestProposalCard
                meetingProposedBy={meetingProposedBy}
                isEverySlotsDeclined={isEverySlotsDeclined}
                interviewDetails={interviewDetails}
                setInterviewDetails={setInterviewDetails}
                availabilitySlots={availabilitySlots}
                setAvailabilitySlots={setAvailabilitySlots}
                groupOfProposals={groupOfProposals}
                isHiringCompanyInvolved={isHiringCompanyInvolved}
                availabilitySlotsRef={availabilitySlotsRef}
                isInviteSend={isInviteSend}
                hasChanges={
                  !isDeepEqual(availabilitySlotsRef.current, availabilitySlots)
                }
                isAssignedHiringTeamMember={isAssignedHiringTeamMember}
              />
            ) : null}

            {/* Past Proposals */}
            {groupOfProposals?.length &&
            !isInviteSend &&
            interviewDetails?.slotSelectionType === "general"
              ? groupOfProposals?.map((proposal, proposalIndex) => {
                  return proposalIndex !== 0 ||
                    (isLatestProposalByMe && !canForwardToRecruiterAgency) ? (
                    <>
                      <PastProposalCard
                        key={proposal?.createdAt}
                        isEverySlotsDeclined={isEverySlotsDeclined}
                        index={proposalIndex}
                        interviewId={interviewId}
                        proposal={proposal}
                        isHiringCompanyInvolved={isHiringCompanyInvolved}
                        isLatestProposalByMe={isLatestProposalByMe}
                        isInviteSend={isInviteSend}
                        isAssignedHiringTeamMember={isAssignedHiringTeamMember}
                      />

                      {/* Propose new time slot button */}
                      {isEverySlotsDeclined && proposalIndex === 0 ? (
                        <div className="flex flex-col items-center mt-5 gap-3">
                          <span className="text-xs font-medium text-light-black">
                            If none of the above time slots match your
                            availability, you may propose new one.
                          </span>
                          <button
                            onClick={handleCounterProposalPoll}
                            className="flex itesm-center justify-center text-white text-xs font-semibold bg-primary rounded-sm py-2 px-4 gap-1"
                          >
                            <PlusCircleIcon
                              className={"w-4 h-4"}
                              fill={"white"}
                            />
                            Propose new times.
                          </button>
                        </div>
                      ) : null}
                    </>
                  ) : null;
                })
              : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingPollDetailPage;
